import { Box, Skeleton, Text } from '@factorialco/deprecated-design-system'
import {
  FinancialWorkspacePayrollConceptsDocument,
  FinancialWorkspacePayrollConceptsQuery,
  PayrollConceptFragment,
} from 'generated/resources/graphql'

import { EntitySelector } from 'modules/core/components/EntitySelector'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { EntityFilter } from 'modules/finance/components/FinancialWorkspacePayroll/components/Filters/EntityFilter'

interface ConceptSelectorProps {
  defaultConceptIds?: number[]
  label: string
  defaultOpen?: boolean
  filter?: boolean
  alwaysOpen?: boolean
  emptyLabel?: string
  mandatory?: boolean
  selectedConcepts?: PayrollConceptFragment[]
  onValueChange: (value: PayrollConceptFragment[]) => void
}

const Item = ({ concept }: { concept: PayrollConceptFragment }) => {
  return (
    <Box gap="s8" flexDirection="row" alignItems="center" key={concept.id}>
      <Text.Normal size="300">{concept.label}</Text.Normal>
    </Box>
  )
}

export const ConceptSelector = ({
  selectedConcepts,
  onValueChange,
  label,
  defaultOpen,
  emptyLabel,
  filter = true,
  alwaysOpen = true,
  mandatory = false,
  defaultConceptIds,
}: ConceptSelectorProps) => {
  const { data: concepts } = useQuery(FinancialWorkspacePayrollConceptsDocument, {
    variables: {
      ids: defaultConceptIds || [],
    },
    skip: !defaultConceptIds?.length,
  })

  const defaultConcepts = concepts?.payroll.conceptsConnection.edges.map((edge) => edge.node)

  const extractConnection = (data: FinancialWorkspacePayrollConceptsQuery) => {
    return data.payroll.conceptsConnection
  }

  const renderEntity = (entity: PayrollConceptFragment) => <Item concept={entity} />

  const renderSkeleton = () => {
    return (
      <Box gap="s8" flexDirection="row" alignItems="center">
        <Skeleton width="s240" height="s32" />
      </Box>
    )
  }

  const filterEntities = (entities: PayrollConceptFragment[]) => entities

  const value: PayrollConceptFragment[] | undefined = useUnsafeMemo(
    () =>
      selectedConcepts?.map((concept) => ({
        id: concept.id,
        name: concept.name || '',
        label: concept.label || '',
        companyId: concept.companyId || 0,
        default: concept.default || false,
        description: concept.description || '',
      })),
    [selectedConcepts]
  )

  const defaultValues: PayrollConceptFragment[] = useUnsafeMemo(() => {
    return (
      defaultConcepts?.map((concept) => ({
        id: concept.id,
        name: concept.name || '',
        label: concept.label || '',
        companyId: concept.companyId || 0,
        default: concept.default || false,
        description: concept.description || '',
      })) || []
    )
  }, [defaultConcepts])

  const valueLabel = (entities: PayrollConceptFragment[]) => {
    const count = entities.length

    if (filter) return <></>
    if (count === 0)
      return (
        <Text.Normal size="300" color="grey400">
          {emptyLabel}
        </Text.Normal>
      )

    const color = count > 0 ? 'grey1000' : 'grey400'

    return (
      <Text.Normal size="300" color={color}>
        {count > 3 ? i18n.t('common.selected') : entities.map((o) => o.label).join(', ')}
      </Text.Normal>
    )
  }

  const selector = () => {
    return (
      <EntitySelector.Multiple
        mandatory={mandatory}
        alwaysOpen={alwaysOpen}
        hideBorder
        variables={{}}
        document={FinancialWorkspacePayrollConceptsDocument}
        hideSearchIcon
        extractConnection={extractConnection}
        renderEntity={renderEntity}
        value={value || defaultValues}
        onChange={onValueChange}
        renderSkeleton={renderSkeleton}
        renderValue={(entities: PayrollConceptFragment[]) => valueLabel(entities)}
        filterEntities={filterEntities}
      />
    )
  }

  if (filter) {
    return (
      <EntityFilter
        value={value || defaultValues}
        onChange={onValueChange}
        defaultOpen={defaultOpen}
        label={label}
      >
        {selector()}
      </EntityFilter>
    )
  }

  return selector()
}
